import { notification } from "@datagame/ui";
import { useRef } from "react";
import Konva from "konva";
import { Stage, Layer, Rect } from "react-konva";
import { getRowStatus } from "../statuses";
import { t } from "i18next";

import styles from "./Result.module.scss";
import { GameVisuals } from "../../../gql/graphql";

type ResultProps = {
  solution: string;
  guesses: string[];
  tries: number;
  theme: GameVisuals;
};

type Colors = {
  correct: string;
  present: string;
  absent: string;
  default: string;
};

const CELL_SIZE = 25;
const GAP_SIZE = 2;

const EmptyResultRow = ({ size, rowNumber, colors }: { size: number; rowNumber: number; colors: Colors }) => {
  const row = new Array(size).fill(null);
  const y = rowNumber * (CELL_SIZE + GAP_SIZE);

  return (
    <>
      {row.map((_, index) => (
        <Rect x={index * (CELL_SIZE + GAP_SIZE)} y={y} width={25} height={25} fill={colors["default"]} key={index} />
      ))}
    </>
  );
};

const ResultRow = ({
  solution,
  guess,
  rowNumber,
  colors,
}: {
  solution: string;
  guess: string;
  rowNumber: number;
  colors: Colors;
}) => {
  const row = getRowStatus(solution, guess);
  const y = rowNumber * (CELL_SIZE + GAP_SIZE);

  return (
    <>
      {row.map((status, index) => (
        <Rect x={index * (CELL_SIZE + GAP_SIZE)} y={y} width={25} height={25} fill={colors[status]} key={index} />
      ))}
    </>
  );
};

export const Result = ({ solution, guesses, tries, theme }: ResultProps) => {
  const stageRef = useRef<Konva.Stage>(null);

  const colors = {
    correct: theme.correctLetter,
    present: theme.presentLetter,
    absent: theme.absentLetter,
    default: theme.defaultLetter,
  };

  const empties = new Array(tries - guesses.length).fill(null);
  const width = CELL_SIZE * solution.length + GAP_SIZE * (solution.length - 1);
  const height = CELL_SIZE * tries + GAP_SIZE * (tries - 1);

  const copyAsImage = async () => {
    const stage = stageRef.current;

    if (!stage) return;

    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = stage.width();
    tempCanvas.height = stage.height();

    const tempCtx = tempCanvas.getContext("2d");

    stage.draw();
    const stageImage = stageRef.current.toCanvas();
    tempCtx?.drawImage(stageImage, 0, 0);

    const blobCallback = (blob: Blob | null) => {
      if (!blob) return;

      navigator.clipboard.write([
        new ClipboardItem({
          [blob.type]: blob,
        }),
      ]);
    };

    notification.success({
      title: t("common:wordle.copyResult.title"),
      description: t("common:wordle.copyResult.description"),
    });

    tempCanvas.toBlob(blobCallback, "image/png");
  };

  return (
    <>
      <Stage className={styles.resultGraph} width={width} height={height} ref={stageRef} onClick={() => copyAsImage()}>
        <Layer>
          {guesses.map((guess, index) => (
            <ResultRow solution={solution} guess={guess} rowNumber={index} key={index} colors={colors} />
          ))}
          {empties.map((_, index) => (
            <EmptyResultRow size={solution.length} rowNumber={index + guesses.length} key={index} colors={colors} />
          ))}
        </Layer>
      </Stage>
      <span className={styles.copy}>{t("common:wordle.copyResult.indicator")}</span>
    </>
  );
};
